import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Clock, Flame } from "lucide-react";
import api from "@/lib/api";
import { PageHero, EmptyState, Reveal } from "@/components/shared";

export const CATEGORIES = [
  { id: "entrees", label: "Entrées" },
  { id: "plats", label: "Plats" },
  { id: "desserts", label: "Desserts" },
  { id: "tapas", label: "Tapas à partager" },
  { id: "cocktails-alcool", label: "Cocktails avec alcool" },
  { id: "cocktails-sans-alcool", label: "Cocktails sans alcool" },
  { id: "vins", label: "Carte des vins" },
  { id: "champagnes", label: "Champagnes" },
];

export default function Carte() {
  const [menu, setMenu] = useState(null);
  const [categories, setCategories] = useState(CATEGORIES);
  const [active, setActive] = useState("entrees");

  useEffect(() => {
    api
      .get("/menu")
      .then((r) => setMenu(r.data))
      .catch(() => setMenu({}));
    api
      .get("/categories")
      .then((r) => {
        if (r.data.length) {
          setCategories(r.data);
          setActive(r.data[0].id);
        }
      })
      .catch(() => {});
  }, []);

  const items = menu?.[active] || [];

  return (
    <div data-testid="carte-page">
      <PageHero overline="La Carte" title="Notre cuisine" testId="carte-hero" />

      <section className="px-6 pb-28 md:px-12 md:pb-40">
        <div className="mx-auto max-w-4xl">
          <Reveal>
            <div
              className="flex flex-wrap gap-2 border-b border-white/10 pb-6"
              data-testid="menu-categories"
            >
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActive(cat.id)}
                  data-testid={`menu-tab-${cat.id}`}
                  className={`rounded-md px-4 py-2.5 text-[12px] font-semibold uppercase tracking-[0.15em] transition-colors duration-300 ${
                    active === cat.id
                      ? "bg-copper text-ivory"
                      : "text-ivory/50 hover:text-gold"
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </Reveal>

          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="mt-14"
            >
              {items.length === 0 ? (
                <EmptyState text="Cette section sera bientôt disponible." />
              ) : (
                <ul className="space-y-10" data-testid="menu-items">
                  {items.map((item) => (
                    <li key={item.id} className="flex gap-6" data-testid={`menu-item-${item.id}`}>
                      {item.photo && (
                        <img
                          src={item.photo}
                          alt={item.name_fr}
                          className="hidden h-20 w-20 shrink-0 rounded-md border border-white/10 object-cover sm:block"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-4">
                          <h3 className="text-base font-semibold tracking-tight text-ivory">
                            {item.name_fr}
                          </h3>
                          <span className="mx-2 hidden flex-1 border-b border-dotted border-white/15 sm:block" />
                          <span className="shrink-0 text-sm font-semibold text-gold">
                            {item.price}
                          </span>
                        </div>
                        {item.description_fr && (
                          <p className="mt-2 max-w-xl text-sm leading-relaxed text-ivory/50">
                            {item.description_fr}
                          </p>
                        )}
                        {(item.spice_level > 0 || item.prep_time || item.allergens || item.pairings || item.supplements) && (
                          <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5" data-testid={`menu-badges-${item.id}`}>
                            {item.spice_level > 0 && (
                              <span className="flex items-center gap-0.5" title={`Niveau de piment ${item.spice_level}/3`}>
                                {[1, 2, 3].map((n) => (
                                  <Flame
                                    key={n}
                                    className={`h-3.5 w-3.5 ${n <= item.spice_level ? "text-copper" : "text-white/15"}`}
                                    strokeWidth={1.5}
                                    fill={n <= item.spice_level ? "currentColor" : "none"}
                                  />
                                ))}
                              </span>
                            )}
                            {item.prep_time && (
                              <span className="flex items-center gap-1 text-[11px] text-ivory/40">
                                <Clock className="h-3 w-3" strokeWidth={1.5} />
                                {item.prep_time}
                              </span>
                            )}
                            {item.allergens && (
                              <span className="text-[11px] text-ivory/40">
                                Allergènes : {item.allergens}
                              </span>
                            )}
                            {item.supplements && (
                              <span className="text-[11px] text-copper/80">
                                Suppléments : {item.supplements}
                              </span>
                            )}
                            {item.pairings && (
                              <span className="text-[11px] italic text-gold/70">
                                S'accorde avec : {item.pairings}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>
    </div>
  );
}
